import React, { ComponentProps, useContext, useMemo, useRef } from 'react';
import { DocumentNode, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import {
  PurchaseOrderDocument,
  ReportTemplate,
  Supplier,
  SupplierAttributeConfig,
  SupplierAuditByField as auditEntity,
  SupplierContact,
  SupplierContactAttributeConfig,
  SupplierNoteType as SupplierNoteTypeEntity,
  SupplierRemittance,
  SystemPrinter,
} from '../../graphql/entities';
import mapStateToFormProps from '../../utils/mapStateToFormProps';
import createForm from '../../components/connectedForm/ConnectedForm';
import { default as Form } from '../../components/connectedForm';
import * as input from '../../components/connectedForm/inputComponents';
import { normalizeUpperCase } from '../../utils/Values';
import componentQuery from '../../utils/componentQuery';
import PurchaseOrderWorkspace from '../purchases/PurchaseOrderWorkspace';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import GridContainer from '../../components/connectedForm/GridContainer';
import AuditByFieldWorkspace from '../company/AuditByFieldWorkspace';
import getIn from '../../components/connectedForm/utils/getIn';
import ContactTab, { IContactTabProps } from '../customer/ContactTab';
import DiaryEventWorkspace from '../diary/DiaryEventWorkspace';
import EmbeddedFormContainer from '../../components/connectedForm/EmbeddedFormContainer';
import PurchaseTransactionEnquiryForm from './PurchaseTransactionEnquiryForm';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import GoodsReceiptLineWorkspace from '../product/GoodsReceiptLineWorkspace';
import AbstractDocumentConfig from '../AbstractDocumentConfig';
import { getEntryCount } from '../../utils/CountUtil';
import ContactCard from '../customer/ContactCard';
import SupplierOverviewTab from '../../views/supplier/SupplierOverviewTab';
import SupplierPriceListWorkspace from '../../views/supplier/SupplierPriceListWorkspace';
import { useCompany } from '../../utils/FetchConfig';
import CurrencyContext from '../../components/Context/CurrencyContext';
import AppContext from '../../components/Context/AppContext';
import { useFormQuery } from '../../graphql/containers/useFormQuery';
import { EntityType, IEntityDocumentConfiguration } from '../../@types/entityTypes';
import ConnectedFormContext, { ConnectedFormContextType } from '../../components/Context/ConnectedFormContext';
import SupplierCarriageCosts from './SupplierCarriageCosts';
import { ConnectedFormPristinePrompt } from '../../components/connectedForm/ConnectedFormPristinePrompt';
import { FormAlertDetails, FormAlertRow } from '../common/FormAlertsTab';
import fieldMetaData, { IEnumMetaData } from '../../graphql/schema/fieldMetaData';
import { IDropdownProps } from '../../@types/dropdown';
import {
  CostViewPermission,
  FilterOperation,
  ICustomer,
  IFilterInput,
  IPurchaseOrder,
  ISupplier,
  ISupplierNotesQueryResult,
  ISupplierNotesQueryVariables,
} from '../../graphql/schema/p4types';
import {
  BlurTextFieldType,
  HrefLink,
  IBlurTextFieldProps,
} from '../../components/connectedForm/BlurTextField/BlurTextField';
import { IGenericArrayProps } from '../../@types/genericArray';
import { IAttributeArrayProps } from '../../components/connectedForm/AttributeArray';
import { IValueFieldProps } from '../../components/connectedForm/ValueField';
import { IImageSelectorProps } from '../../@types/imageSelector';
import { useSupplierTotals } from './useSupplierTotals';
import { IDateFieldProps } from '../../components/connectedForm/DateInput/DateInput';
import MemoEditor, { IMemoEditorType, useMemoEditorProps } from '../../components/connectedForm/MemoEditor/MemoEditor';
import SupplierNotesWorkspaceQuery from '../../graphql/queries/supplier/SupplierNotesWorkspaceQuery.graphql';
import useEntityCacheUpdate from '../../graphql/hooks/useEntityCacheUpdate';
import { ICheckboxInputProps } from '../../components/connectedForm/CheckboxInput/CheckboxInput';
import OutlookTab from '../common/OutlookTab';
import { useHistory } from 'react-router';
import { getEmailAddresses } from '../../utils/getContactEmailAddresses';
import { Paths } from '../../@types/keyOfNested';
import { useFullUserDetails } from '../../utils/hooks';
import { ConnectedAddress } from '../../components/connectedForm/ConnectedAddress';
import { PurchaseEdiConfiguration } from './PurchaseEdiConfiguration';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import useInstrastatHints from '../../utils/useInstrastatHints';

const {
  default: defaultQuery,
  actions,
  formTitle,
  dropdown,
  workspaceUrl,
  update,
  instance,
  fragment,
  type,
  temporarySave,
} = Supplier;

const STATUS_FLAGS = [
  {
    key: 'accountStatus.held',
    status: [{ value: true, style: 'error', title: 'HELD' }],
  },
  {
    key: 'foreignCurrency',
    status: [
      {
        value: true,
        style: 'info',
        icon: 'account_balance',
        title: '',
        titleKey: 'currency.isoCode.codeAlpha3',
      },
    ],
  },
];

const templateFilter = (): Array<IFilterInput> => [
  {
    filterOperation: FilterOperation.Equals,
    filterParameters: {
      field: 'template',
      value: 'true',
    },
  },
];

const SupplierContactCard = ({ rowIndex }: { rowIndex: number }) => {
  return <Form.Field dataid={`contacts.${rowIndex}`} component={ContactCard} />;
};

const SupplierContactDetails = ({ rowIndex }: { rowIndex: number }) => {
  const { readOnly, values, change, initialized } = useContext(ConnectedFormContext);

  return !initialized ? null : (
    <Form.Field<IContactTabProps & { entity: EntityType }>
      dataid={`contacts.${rowIndex}`}
      component={ContactTab}
      index={rowIndex}
      readOnly={readOnly}
      values={values as ICustomer | ISupplier}
      change={change}
      type={'supplier'}
      attributeConfig={SupplierContactAttributeConfig}
      temporary={true}
      forceUpdate={true}
      entity={SupplierContact}
    />
  );
};

const SupplierForm = ({
  activeTabId,
  change,
  values,
  match,
  overrideParams,
  pageView,
}: {
  activeTabId: string;
  change: ConnectedFormContextType['change'];
  values: ISupplier;
  match: { params?: any };
  overrideParams?: any;
  pageView?: boolean;
}) => {
  const fullUserDetails = useFullUserDetails();

  const id = match?.params?.id;
  const newAccount = !(id && id !== 'New');

  let filterValue = null;
  if (!newAccount) {
    filterValue = id;
  }

  const supplierDotIdFilter: Array<IFilterInput> = [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'supplier.id',
        value: filterValue,
      },
    },
  ];

  const supplierIdFilter: Array<IFilterInput> = [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'id',
        value: filterValue,
      },
    },
  ];

  const tabPanelRef = useRef();
  const { user } = useContext(AppContext);

  const { orders, outstandingSum, awaitingApprovalSum } = useSupplierTotals(supplierDotIdFilter, filterValue);

  const memoEditorProps = useMemoEditorProps<ISupplierNotesQueryResult, ISupplierNotesQueryVariables>(
    SupplierNotesWorkspaceQuery,
    IMemoEditorType.Supplier,
    {
      predefinedFilters: [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'supplier.id',
            value: values.id,
          },
        },
      ],
    }
  );

  const emailAddresses = useMemo(() => {
    return getEmailAddresses(values);
  }, [values.email, values.contacts]);

  const getDocumentDefaultList = () => {
    const configs = [PurchaseOrderDocument.configuration, SupplierRemittance.configuration];

    return configs.map((configuration) => {
      return {
        ...configuration,
        config: getIn(values, configuration?.supplierField),
      };
    });
  };

  const renderDocumentDetails = (rowData: IEntityDocumentConfiguration) => {
    if (rowData === null || rowData.supplierField === null) return null;
    const field = rowData.supplierField;

    const config = rowData.config;

    if (!config) return null;

    const reportTypeFilters: Array<IFilterInput> = [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'reportType',
          value: rowData.reportType,
        },
      },
    ];

    const documentRequired = (
      <Form.Field label="Required" dataid={`${field}.documentRequired`} component={input.Checkbox} />
    );
    let outputConfig;

    const output = (
      <Form.Field<IDropdownProps>
        label="Output"
        dataid={`${field}.output`}
        component={input.Dropdown}
        options={(fieldMetaData.DocumentOutputType as IEnumMetaData).enumValues}
        enum={true}
      />
    );
    const layout = (
      <Form.Field<IDropdownProps>
        label="Layout"
        dataid={`${field}.layout`}
        component={input.Dropdown}
        {...ReportTemplate.dropdown}
        predefinedFilters={reportTypeFilters}
      />
    );
    if (config.output === 'SYSTEM_PRINTER') {
      outputConfig = (
        <Form.Field<IDropdownProps>
          label="System Printer"
          dataid={`${field}.systemPrinterId`}
          component={input.Dropdown}
          valueOnly
          {...SystemPrinter.dropdown}
        />
      );
    }

    const supplierContacts = values.contacts;
    const documentContacts = supplierContacts
      ? supplierContacts.filter((contact) => {
          return contact?.[rowData.receiveFlag as unknown as keyof typeof contact];
        })
      : [];

    const recipients = (
      <QueryDropdown
        data-testid={'document-recipients'}
        label={'Recipients'}
        {...SupplierContact.dropdown}
        multi={true}
        deletable={true}
        predefinedFilters={supplierDotIdFilter}
        value={documentContacts}
        onChange={(values: Array<ISupplier> | null) => {
          let contacts = supplierContacts;
          if (!contacts) return;

          contacts = contacts.map((contact) => {
            (contact as unknown as { [key: string]: boolean })[rowData.receiveFlag as string] =
              values !== null && values.findIndex((value) => !value.deleted && value.id === contact!.id) >= 0;
            return contact;
          });
          change('contacts', contacts);
        }}
      />
    );

    return (
      <Form.Fieldset cols={1}>
        <Form.Legend label={rowData.title} />
        {documentRequired}
        {output}
        {layout}
        {outputConfig}
        {recipients}
      </Form.Fieldset>
    );
  };

  const archiveWarningSuffix = useMemo(() => {
    const warning =
      values?.numProductsAsMainSupplier && values.numProductsAsMainSupplier > 0
        ? `Supplier is main supplier on ${values?.numProductsAsMainSupplier} product(s).`
        : '';
    return warning;
  }, [values?.numProductsAsMainSupplier]);

  const templateToForm = (value: ISupplier) => {
    const vars = { id: value.id };
    componentQuery(defaultQuery, vars, true).then(({ response: data }: any) => {
      change(false, data.defaultRecord);
    });
  };

  const isNotNew = (match?.params?.id || overrideParams?.id) !== 'New';
  const company = useCompany();
  const current = company?.currentFinancialPeriod?.name || 'Current';
  const period1 = company?.month1FinancialPeriod?.name || 'Month +1';
  const period2 = company?.month2FinancialPeriod?.name || 'Month +2';
  const period3 = company?.month3FinancialPeriod?.name || 'Month +3';
  const period4 = company?.month4FinancialPeriod?.name || 'Month +4';
  const attachmentsCount = getEntryCount(values.attachments);

  const noDeleteOption = values.balance
    ? (values.balance?.balance !== 0 && values.balance?.balance !== null) ||
      orders?.orderValue !== 0 ||
      outstandingSum !== 0 ||
      awaitingApprovalSum !== 0
    : false;

  let activeTab = !newAccount ? 'overview' : 'main';

  if (activeTabId?.length > 0) {
    activeTab = activeTabId;
  }

  const availableColumnsFilter = (column: IEditableDataGridColumnDef): boolean => {
    return (
      column.fieldName !== 'supplier.reference' &&
      column.fieldName !== 'supplier.name' &&
      column.fieldName !== 'supplier' &&
      column.fieldName !== 'supplier.email' &&
      column.fieldName !== 'supplier.telephoneNumber' &&
      column.fieldName !== 'supplier.address.country.description' &&
      column.fieldName !== 'supplier.supplierPurchaseOrderDocumentDefault.output'
    );
  };

  const availableFiltersFilter = (filter: IEditableDataGridColumnDef): boolean => {
    return (
      filter.fieldName !== 'supplier.reference' &&
      filter.fieldName !== 'supplier.name' &&
      filter.fieldName !== 'supplier' &&
      filter.fieldName !== 'supplier.email' &&
      filter.fieldName !== 'supplier.telephoneNumber' &&
      filter.fieldName !== 'supplier.address.country.description' &&
      filter.fieldName !== 'supplier.supplierPurchaseOrderDocumentDefault.output'
    );
  };

  const fieldNames: Array<Paths<IPurchaseOrder>> = [
    'orderNumber',
    'orderDate',
    'orderReference',
    'status',
    'targetDate',
  ];

  if (fullUserDetails?.companyUser?.costViewPermission === CostViewPermission.All) {
    fieldNames.push('headerTotal');
  }

  const { modeOfTransportHint, natureOfTransactionHint, customsProcedureCodeHint } = useInstrastatHints();

  return (
    <>
      <ConnectedFormPristinePrompt />
      <CurrencyContext.Provider value={{ currency: values?.currency || company?.baseCurrency }}>
        <Form.FormContainer>
          <Form.Header
            entityHdr="reference"
            entityHdr2="name"
            component={Form.HeaderBlock}
            archive={true}
            actionButtons={actions}
            statusFlags={STATUS_FLAGS}
            noDeleteOption={noDeleteOption}
            archiveWarningSuffix={archiveWarningSuffix}
            allowRestoreOption={true}
          />
          <Form.Form>
            <Form.TabPanel ref={tabPanelRef} formName={type} active={activeTab}>
              <Form.TabContent id="overview" label="Overview" shouldRender={!newAccount}>
                <SupplierOverviewTab
                  id={values.id}
                  tabPanelRef={tabPanelRef}
                  sidebar={false}
                  orders={orders}
                  outstandingSum={outstandingSum}
                  awaitingApprovalSum={awaitingApprovalSum}
                  values={values}
                />
              </Form.TabContent>
              <Form.TabContent id="main" label="General">
                <Form.Row>
                  {!values.id ? (
                    <Form.Fieldset cols={1}>
                      <Form.Legend label="Template" />
                      <QueryDropdown
                        data-testid={'template-select'}
                        value=""
                        label="Template"
                        customFilter={templateFilter}
                        onChange={templateToForm}
                        {...(dropdown as Partial<ComponentProps<typeof QueryDropdown>>)}
                        placeholder=""
                        preLoad={true}
                      />
                      <Form.Field dataid="template" component={input.Checkbox} label="Save as template" />
                    </Form.Fieldset>
                  ) : null}
                  <Form.Fieldset cols={1 / 2}>
                    <Form.Legend label="Address Information" />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="reference"
                      label="Reference"
                      component={input.BlurTextField}
                      normalize={normalizeUpperCase}
                      spellCheck="false"
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="oldReference"
                      component={input.BlurTextField}
                      label="Old Reference"
                      normalize={normalizeUpperCase}
                      spellCheck="false"
                      readOnly={true}
                      hint={'Imported supplier reference from previous/other system.'}
                      hideWhenEmpty={true}
                    />
                    <Form.Field dataid="name" label="Name" component={input.BlurTextField} autoFocus={newAccount} />
                    <ConnectedAddress
                      dataid={'address'}
                      labelOverrides={{ line1: 'Address', line2: '', line3: '', line4: '', line5: '' }}
                      loqateProps={{ companyDataid: 'name' }}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={1 / 2}>
                    <Form.Legend label="Contact Information" />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="email"
                      label="Email"
                      component={input.BlurTextField}
                      hyperlink={HrefLink.MailTo}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="telephoneNumber"
                      label="Telephone Number"
                      component={input.BlurTextField}
                      hyperlink={HrefLink.Tel}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="telephoneNumber2"
                      component={input.BlurTextField}
                      label=""
                      hyperlink={HrefLink.Tel}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="mobileNumber"
                      component={input.BlurTextField}
                      label="Mobile Number"
                      hyperlink={HrefLink.Tel}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="faxNumber"
                      label="Fax Number"
                      component={input.BlurTextField}
                      hyperlink={HrefLink.Tel}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="faxNumber2"
                      component={input.BlurTextField}
                      label=""
                      hyperlink={HrefLink.Tel}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="website"
                      component={input.BlurTextField}
                      label="Website"
                      hyperlink
                    />
                  </Form.Fieldset>
                </Form.Row>
                <Form.Row>
                  <Form.Fieldset cols={1 / 2}>
                    <Form.Legend label="Other Information" />
                    <Form.Field dataid="createdBy" component={input.BlurTextField} label="Created By" readOnly={true} />
                    <Form.Field<IDateFieldProps>
                      dataid="createdAt"
                      component={input.DateField}
                      label="Date/Time Created"
                      dateFormat="iii do MMM yyyy HH:mm"
                      readOnly={true}
                    />
                    <Form.Field
                      dataid="updatedBy"
                      component={input.BlurTextField}
                      label="Last Updated By"
                      readOnly={true}
                    />
                    <Form.Field<IDateFieldProps>
                      dataid="updatedAt"
                      component={input.DateField}
                      label="Date/Time Updated"
                      dateFormat="iii do MMM yyyy HH:mm"
                      readOnly={true}
                    />
                    <Form.Field<IDateFieldProps>
                      dataid="lastContactDate"
                      component={input.DateField}
                      label="Last Contact"
                      dateFormat="iii do MMM yyyy"
                      readOnly={true}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent
                id="outlook"
                label="Outlook"
                shouldRender={
                  isNotNew &&
                  emailAddresses.length > 0 &&
                  fullUserDetails?.flags?.outlookIntegrationEnabled &&
                  user?.msftAccessTokenSet
                }
              >
                <OutlookTab emailAddresses={emailAddresses} />
              </Form.TabContent>
              <Form.TabContent id="headOffice" label="Head Office">
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Field dataid="headOffice" component={input.Dropdown} label="Head Office" />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent id="contacts" label="Contacts">
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Field<IGenericArrayProps>
                      dataid="contacts"
                      component={input.GenericArray}
                      label="Contacts"
                      renderDetails={SupplierContactDetails}
                      renderRow={SupplierContactCard}
                      title={'Contact Details'}
                      enableNewRows={true}
                      renderRowIsComponent={true}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent id="attributes" label="Attributes" permissionReference={'SUPPLIERS_ATTRIBUTES_VIEW'}>
                <Form.Row>
                  <Form.Fieldset cols={5 / 8}>
                    <Form.Legend label="Attributes" />
                    <Form.Field<IAttributeArrayProps>
                      dataid="attributeValues"
                      component={input.AttributeArray}
                      label="Attributes"
                      query={SupplierAttributeConfig.list}
                      type={'supplier'}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent id="credit" label="Credit Control">
                <Form.Row>
                  <Form.Fieldset cols={4 / 8}>
                    <Form.Legend label="Account Status" />
                    <Form.Field dataid="accountStatus" component={input.Dropdown} label="Account Status" />
                    <Form.Field<IDateFieldProps>
                      dataid="lastTransactionDate"
                      component={input.DateField}
                      label="Last Transaction"
                      dateFormat="iii do MMM yyyy"
                      readOnly={true}
                    />
                    <Form.Field<IDateFieldProps>
                      dataid="lastInvoiceDate"
                      component={input.DateField}
                      label="Last Invoice"
                      dateFormat="iii do MMM yyyy"
                      readOnly={true}
                    />
                    <Form.Field<IDateFieldProps>
                      dataid="lastPaymentDate"
                      component={input.DateField}
                      label="Last Payment"
                      dateFormat="iii do MMM yyyy"
                      readOnly={true}
                    />
                    <Form.Legend label="Payment Terms/Method" />
                    <Form.Field dataid="paymentTerm" component={input.Dropdown} label="Terms" rawLabel={true} />
                    <Form.Field dataid="paymentMethod" component={input.Dropdown} label="Method" />
                    <Form.Field dataid="paymentPriority" component={input.Dropdown} label="Priority" />
                    <Form.Field<ICheckboxInputProps>
                      dataid="overrideHeadOfficePaymentTerm"
                      component={input.Checkbox}
                      hint={'Supplier details will default to head office for invoicing'}
                      label="Override Head Office Payment Terms?"
                    />
                    <Form.Legend label="Credit" />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="creditLimit"
                      component={input.BlurTextField}
                      label="Credit Limit"
                      decimals={2}
                      type={BlurTextFieldType.Currency}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="creditAgencyReference"
                      component={input.BlurTextField}
                      label="Agency Reference"
                    />
                    <Form.Field dataid="dunsNumber" component={input.BlurTextField} label="DUNS Number" />
                    <Form.Field dataid="creditReviewDate" component={input.DateField} label="Last Review Date" />
                  </Form.Fieldset>
                  <Form.Fieldset cols={4 / 8}>
                    <Form.Legend label="Account Balances" />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.balance"
                      component={input.ValueField}
                      label="Total Balance"
                      reformatLabel={false}
                      decimals={2}
                      type={BlurTextFieldType.Currency}
                    />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.future"
                      component={input.ValueField}
                      reformatLabel={false}
                      label="Future"
                      decimals={2}
                      type={'currency'}
                    />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.current"
                      component={input.ValueField}
                      reformatLabel={false}
                      label={current}
                      decimals={2}
                      type={'currency'}
                    />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.month1"
                      component={input.ValueField}
                      reformatLabel={false}
                      label={period1}
                      decimals={2}
                      type={'currency'}
                    />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.month2"
                      component={input.ValueField}
                      reformatLabel={false}
                      label={period2}
                      decimals={2}
                      type={'currency'}
                    />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.month3"
                      component={input.ValueField}
                      reformatLabel={false}
                      label={period3}
                      decimals={2}
                      type={'currency'}
                    />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.month4"
                      component={input.ValueField}
                      reformatLabel={false}
                      label={period4}
                      decimals={2}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent
                id="transactions"
                label="Transactions"
                key="transactions"
                shouldRender={isNotNew}
                permissionReference={'SUPPLIER_TRANSACTIONS_VIEW'}
              >
                <EmbeddedFormContainer>
                  {values.id ? (
                    <PurchaseTransactionEnquiryForm
                      embedded={true}
                      supplier={{ id: values.id, currency: values.currency } as unknown as ISupplier}
                      tourVisible={true}
                    />
                  ) : null}
                </EmbeddedFormContainer>
              </Form.TabContent>
              <Form.TabContent id="events" label="Tasks" shouldRender={isNotNew} permissionReference={'TASK_VIEW'}>
                <GridContainer>
                  <DiaryEventWorkspace
                    embeddedView={true}
                    pageView={pageView}
                    predefinedFilters={supplierDotIdFilter}
                    viewScreenName="/Diary/Task/Modal"
                    removeAddButton={true}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent id="vat" label="VAT/Nominal">
                <Form.Row>
                  <Form.Fieldset cols={5 / 8}>
                    <Form.Legend label="VAT" />
                    <Form.Field<IDropdownProps>
                      dataid="vatBusinessCategory"
                      label="Business VAT Status"
                      component={input.Dropdown}
                      labelCols={5 / 12}
                      inputCols={7 / 12}
                      reformatLabel={false}
                    />
                    <Form.Field<IDropdownProps>
                      dataid="vatProductCategory"
                      label="Product VAT Category"
                      component={input.Dropdown}
                      labelCols={5 / 12}
                      inputCols={7 / 12}
                      reformatLabel={false}
                    />
                    <Form.Field<IDropdownProps>
                      dataid="carriageVatProductCategory"
                      label="Carriage Vat Category"
                      component={input.Dropdown}
                      labelCols={5 / 12}
                      inputCols={7 / 12}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="vatRegistrationNumber"
                      component={input.BlurTextField}
                      label="VAT Registration Number"
                      labelCols={5 / 12}
                      inputCols={7 / 12}
                      reformatLabel={false}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={5 / 8}>
                    <Form.Legend label="Nominal" />
                    <Form.Field<IDropdownProps>
                      dataid="nominalBusinessCategory"
                      label="Nominal Business Category"
                      component={input.Dropdown}
                      labelCols={5 / 12}
                      inputCols={7 / 12}
                    />
                    <Form.Field<IDropdownProps>
                      dataid="nominalSupplierAccountSet"
                      label="Supplier Account Set"
                      component={input.Dropdown}
                      labelCols={5 / 12}
                      inputCols={7 / 12}
                    />
                    <Form.Field<IDropdownProps>
                      dataid="purchaseNominal"
                      label="Manual Invoice/Credit Nominal"
                      labelCols={5 / 12}
                      inputCols={7 / 12}
                      component={input.Dropdown}
                      hint={
                        <>
                          <li>Nominal to use for manually added Purchase Invoices/Credits</li>
                          <li>
                            If invoicing a PO, nominal account set will used, followed by the Supplier Account Set -
                            purchase nominal.
                          </li>
                        </>
                      }
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent
                id="notesTab"
                label="Notes"
                order="5"
                badge={memoEditorProps.queryProps.data?.listData?.count}
                type="info"
                onlyRenderWhenActive={true}
                shouldRender={!newAccount}
              >
                <MemoEditor noteTypeEntity={SupplierNoteTypeEntity} {...memoEditorProps} />
              </Form.TabContent>
              <Form.TabContent
                id="attachments"
                label="Attachments"
                badge={attachmentsCount}
                onlyRenderWhenActive={true}
              >
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Legend label="Main image" />
                    <Form.Field<IImageSelectorProps>
                      dataid="image.fileName"
                      component={input.ImageSelector}
                      includeLabel={false}
                    />
                  </Form.Fieldset>
                </Form.Row>
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Legend label="Attachments" />
                    <Form.Field dataid="attachments" component={input.GridUploader} />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent id="documentDefaults" label="Documents">
                <AbstractDocumentConfig
                  forceUpdate={true}
                  documentDefaultList={getDocumentDefaultList()}
                  renderDocumentDetails={renderDocumentDetails}
                />
              </Form.TabContent>
              <Form.TabContent
                id="purchases"
                label="Orders"
                key="purchases"
                shouldRender={isNotNew}
                permissionReference={'PURCHASING_ORDERS_VIEW'}
              >
                <GridContainer>
                  <PurchaseOrderWorkspace
                    embeddedView={true}
                    pageView={pageView}
                    additionalVariables={{ excludeSupplier: true }}
                    predefinedFilters={supplierDotIdFilter}
                    viewScreenName="/purchases/Orders/Modal"
                    removeAddButton={true}
                    availableFiltersFilter={availableFiltersFilter}
                    availableColumnsFilter={availableColumnsFilter}
                    fieldNameOverrides={fieldNames}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent
                id="goodsReceiptLines"
                label="Vendor Analysis"
                key="goodsReceiptLines"
                shouldRender={isNotNew}
              >
                <GridContainer>
                  <GoodsReceiptLineWorkspace
                    embeddedView={true}
                    pageView={pageView}
                    additionalVariables={{ excludeSupplier: true }}
                    predefinedFilters={[
                      {
                        filterOperation: FilterOperation.Equals,
                        filterParameters: {
                          field: 'goodsReceipt.supplier.id',
                          value: match?.params?.id,
                        },
                      },
                    ]}
                    viewScreenName="/product/GoodsReceiptLine/Modal"
                    removeAddButton={true}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent
                id="audit"
                label="Audit"
                key="audit"
                shouldRender={isNotNew}
                permissionReference={'SUPPLIER_AUDIT_VIEW'}
              >
                <GridContainer>
                  <AuditByFieldWorkspace
                    embeddedView={true}
                    pageView={pageView}
                    predefinedFilters={supplierIdFilter}
                    viewScreenName="/supplier/SupplierAudit/Workspace"
                    entity={auditEntity}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent
                id="priceList"
                label="Price List"
                key="priceList"
                shouldRender={isNotNew && fullUserDetails?.companyUser?.costViewPermission === CostViewPermission.All}
                permissionReference={'SUPPLIER_PRICE_LIST_VIEW'}
              >
                <GridContainer>
                  <SupplierPriceListWorkspace
                    embeddedView={true}
                    pageView={pageView}
                    predefinedFilters={supplierIdFilter}
                    viewScreenName="/suppliers/SupplierPriceList/Workspace"
                    additionalVariables={{
                      supplier: normalizeVariables(values),
                    }}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent id="purchaseDefaults" label="Purchase Defaults" key="purchaseDefaults">
                <Form.Legend label="Purchase Defaults" />
                <Form.Field
                  dataid="defaultLeadTimeDays"
                  label="Default Lead Time (Days)"
                  component={input.BlurTextField}
                />
                <Form.Field dataid="currency" label="Currency" component={input.Dropdown} readOnly={isNotNew} />
                <Form.Legend label="Minimum Order" />
                <Form.Field
                  dataid="minimumOrderValue"
                  component={input.BlurTextField}
                  label="Minimum Order Value"
                  type={'currency'}
                />
                <SupplierCarriageCosts />
                <PurchaseEdiConfiguration supplier={values} />
                <Form.Legend label="Intrastat / Customs / International Trade" />
                <Form.Field<IDropdownProps>
                  dataid="intrastatDetails.termsOfDelivery"
                  component={input.Dropdown}
                  label="Default Terms of Delivery"
                  clearable={true}
                />
                <Form.Field<IBlurTextFieldProps>
                  dataid="intrastatDetails.natureOfTransactionCode"
                  label="Default Nature of Transaction"
                  component={input.BlurTextField}
                  hint={natureOfTransactionHint}
                  type={BlurTextFieldType.FluentMask}
                  mask="99"
                />
                <Form.Field<IBlurTextFieldProps>
                  dataid="intrastatDetails.customsProcedureCode"
                  label="Default Customs Procedure Code"
                  component={input.BlurTextField}
                  hint={customsProcedureCodeHint}
                  type={BlurTextFieldType.FluentMask}
                  mask="99-99-999"
                />
                <Form.Field<IBlurTextFieldProps>
                  dataid="intrastatDetails.modeOfTransportCode"
                  label="Default Mode Of Transport Code"
                  component={input.BlurTextField}
                  hint={modeOfTransportHint}
                  type={BlurTextFieldType.FluentMask}
                  mask="9"
                />
              </Form.TabContent>
              <Form.TabContent id="supplier_alerts" label="Alerts">
                <Form.Field<IGenericArrayProps>
                  dataid={'formAlerts'}
                  component={input.GenericArray}
                  label="Alerts"
                  renderRow={FormAlertRow}
                  renderDetails={FormAlertDetails}
                  enableNewRows={true}
                  forceUpdate={true}
                  renderRowIsComponent={true}
                  detailProps={{ type: 'Supplier' }}
                />
              </Form.TabContent>
            </Form.TabPanel>
          </Form.Form>
        </Form.FormContainer>
      </CurrencyContext.Provider>
    </>
  );
};

const ConnectedSupplierForm: any = createForm({
  form: type,
  name: formTitle,
  entity: Supplier,
  queryFragment: fragment,
  defaultReturnUrl: workspaceUrl,
  shouldComponentUpdate: (props, nextProps) => {
    return props !== nextProps || getIn(props, 'values.contacts') !== getIn(nextProps, 'values.contacts');
  },
})(SupplierForm);

export default ({ temporaryDefault, overrideParams, detailView, ...props }: any) => {
  const formProps = useSelector((store) => mapStateToFormProps(store, props));
  const formDefault = useFormQuery({ default: defaultQuery, instance: instance } as EntityType, {
    duplicate: true,
    overrideParams,
  });
  const cacheUpdate = useEntityCacheUpdate('suppliers', 'supplier', { detailView });
  const [formUpdate] = useMutation(update as DocumentNode, {
    update: cacheUpdate,
    refetchQueries: [SupplierNotesWorkspaceQuery],
  });
  const [formTemporaryUpdate] = useMutation(temporarySave as DocumentNode);

  const history = useHistory<{ activeTabId?: string }>();

  let activeTab = history.location?.state?.activeTabId;

  if (props?.activeTabId?.length > 0) {
    activeTab = props?.activeTabId;
  }

  return (
    <ConnectedSupplierForm
      {...formProps}
      {...props}
      formUpdate={formUpdate}
      formTemporaryUpdate={formTemporaryUpdate}
      formData={formDefault}
      activeTabId={activeTab}
    />
  );
};
